import React from "react";
import { motion } from "framer-motion";
import { skillsData, toolsData } from "../lib/ui";

// Duplicate tools for smooth infinite marquee
const marqueeTools = [...toolsData, ...toolsData, ...toolsData];

export default function Skills() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-5 lg:gap-6">
      
      {/* --- Skill Constellation --- */}
      <motion.div
        className="md:col-span-12 lg:col-span-7 glass-card rounded-[32px] p-6 sm:p-10 flex flex-col justify-center relative overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-app-accent/20 rounded-full blur-[80px] -mr-32 -mt-32 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-app-secondary/20 rounded-full blur-[60px] -ml-20 -mb-20 pointer-events-none" />

        <div className="mb-8 z-10">
          <h3 className="font-display text-2xl sm:text-3xl font-bold text-app-text mb-2">
            Professional Skills
          </h3>
          <p className="text-sm text-app-mute">
            A comprehensive overview of my core competencies and expertise.
          </p>
        </div>

        <div className="flex flex-wrap gap-3 z-10">
          {skillsData.map((skill, index) => {
            let colors = "";
            if (skill.type === "primary") {
              colors = "bg-app-accent text-white shadow-glow border-app-accent-dark";
            } else if (skill.type === "secondary") {
              colors = "bg-app-panel text-app-text border-app-border shadow-sm hover:border-app-secondary";
            } else {
              colors = "bg-app-secondary/10 text-app-secondary border-app-secondary/30 border-dashed";
            }

            return (
              <motion.div
                key={skill.name}
                className={`px-5 py-2.5 rounded-full text-sm font-medium border cursor-default shadow-sm transition-transform duration-300 hover:scale-105 ${colors}`}
                initial={{ opacity: 0, scale: 0.8, y: 10 }}
                whileInView={{ opacity: 1, scale: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  type: "spring",
                  stiffness: 260,
                  damping: 20,
                  delay: index * 0.05,
                }}
                whileHover={{ y: -3 }}
              >
                {skill.name}
              </motion.div>
            );
          })}
        </div>
      </motion.div>

      {/* --- Tools Infinite Marquee --- */}
      <motion.div
        className="md:col-span-12 lg:col-span-5 glass-card rounded-[32px] overflow-hidden flex flex-col"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <div className="p-6 sm:p-8 pb-4">
          <h3 className="font-display text-xl font-bold text-app-text">
            Software Proficiency
          </h3>
          <p className="text-xs text-app-mute mt-1">
            Tools and platforms that I use for daily productivity.
          </p>
        </div>

        {/* Marquee Container */}
        <div className="relative flex-1 flex flex-col justify-center py-8 border-y border-app-border bg-app-soft/50">
          {/* Fading Edges */}
          <div className="absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-[#020211] to-transparent z-10 pointer-events-none" />
          <div className="absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-[#020211] to-transparent z-10 pointer-events-none" />

          {/* Scrolling Track */}
          <div className="flex flex-row flex-nowrap w-max animate-marquee">
            <div className="flex flex-row flex-nowrap flex-none items-center gap-6 px-2">
              {marqueeTools.slice(0, toolsData.length).map((tool, i) => (
                <div key={i} className="flex flex-col items-center gap-2 flex-shrink-0 group">
                  <div 
                    className="w-16 h-16 rounded-2xl bg-app-panel border border-app-border shadow-sm flex items-center justify-center text-3xl group-hover:border-app-accent group-hover:shadow-glow transition-all duration-300 group-hover:-translate-y-1"
                    role="img"
                    aria-label={`${tool.name} icon`}
                  >
                    {tool.icon}
                  </div>
                  <span className="text-[11px] font-semibold text-app-mute group-hover:text-app-accent transition-colors">
                    {tool.name}
                  </span>
                </div>
              ))}
            </div>
            <div className="flex flex-row flex-nowrap flex-none items-center gap-12 px-6">
              {marqueeTools.slice(toolsData.length, toolsData.length * 2).map((tool, i) => (
                <div key={i} className="flex flex-col items-center gap-2 flex-shrink-0 group">
                  <div 
                    className="w-16 h-16 rounded-2xl bg-app-panel border border-app-border shadow-sm flex items-center justify-center text-3xl group-hover:border-app-accent group-hover:shadow-glow transition-all duration-300 group-hover:-translate-y-1"
                    role="img"
                    aria-label={`${tool.name} icon`}
                  >
                    {tool.icon}
                  </div>
                  <span className="text-[11px] font-semibold text-app-mute group-hover:text-app-accent transition-colors">
                    {tool.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>

      {/* --- Currently Learning Banner --- */}
      <motion.div
        className="md:col-span-12 glass-card rounded-[32px] p-6 sm:p-8 bg-gradient-to-r from-app-panel via-[#0e0e26] to-[#0a0a20] border-none flex flex-col md:flex-row items-center justify-between gap-6 relative overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.3 }}
      >
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 pointer-events-none" />
        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-64 h-64 bg-app-secondary/20 rounded-full blur-[80px] pointer-events-none" />

        <div className="flex items-center gap-5 z-10 w-full md:w-auto">
          <div className="w-14 h-14 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-md shadow-inner flex items-center justify-center flex-shrink-0 text-2xl text-app-secondary">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <div>
            <div className="flex items-center gap-3 mb-1">
              <p className="text-lg font-display font-bold text-white">
                Qualifications
              </p>
              <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-app-secondary/20 border border-app-secondary/30">
                <span className="w-1.5 h-1.5 rounded-full bg-app-secondary-dark animate-pulse" />
                <span className="text-[10px] font-bold uppercase tracking-wider text-app-secondary-dark">Certified</span>
              </div>
            </div>
            <p className="text-sm text-app-mute font-medium">
              Driven by a desire to leverage my skills for innovation.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 z-10 w-full md:w-auto">
          {["Self-motivated", "Quick Learner", "Work Ethics"].map((item) => (
            <span key={item} className="px-4 py-2 rounded-xl text-xs font-semibold bg-white/5 border border-white/10 text-app-text backdrop-blur-sm">
              {item}
            </span>
          ))}
        </div>
      </motion.div>
    </div>
  );
}
